import { useCallback, useEffect, useRef } from 'react';
import { getGenericChannelId } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { getCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { useCommonReply } from './useCommonReply';
function useUpdateReplyChannelOnChannelIdChange({
  onChangeReplyChannel,
  reply
}) {
  const currentCommonMessage = getCommonMessage(reply);
  const currentMessageChannelId = getGenericChannelId(currentCommonMessage);
  const stableOnChangeReplyChannel = useRef(onChangeReplyChannel);
  stableOnChangeReplyChannel.current = onChangeReplyChannel;
  useEffect(() => {
    if (stableOnChangeReplyChannel.current) {
      stableOnChangeReplyChannel.current(currentMessageChannelId || 'UNSET');
    }
  }, [currentMessageChannelId]);
}
/**
 * @description a hook that decorates the passed change handlers
 * with internal state management in order to do things like
 * reset reply state on submission and update reply state on change.
 */
export const useCommonReplyForEditor = ({
  emptyReply,
  onSubmit,
  reconcileReplies,
  onChangeReplyChannel
}) => {
  const [reply, setReply, resetReply] = useCommonReply({
    emptyReply,
    reconcileReplies
  });
  useUpdateReplyChannelOnChannelIdChange({
    onChangeReplyChannel,
    reply
  });
  const handleSubmit = useCallback((newReply, textOptions) => {
    // simple check to make sure communicator reply object is being used for submission instead of the hook referenced reply object used by inline composer
    const {
      html,
      plainText,
      options = {}
    } = textOptions || newReply;
    const replyToSubmit = textOptions ? newReply : reply;
    onSubmit(replyToSubmit, {
      html,
      plainText,
      communicatorType: 'reply-editor',
      replyType: 'reply',
      options
    });
    resetReply();
  }, [onSubmit, resetReply, reply]);
  return {
    onChange: setReply,
    onSubmit: handleSubmit,
    reply
  };
};